import { X } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import { Drawer as Vaul, type DialogProps as VaulRootProps } from "vaul";
import { Flex } from "../flex/Flex.js";
import { IconButton } from "../icon-button/IconButton.js";
import { useThemeContext } from "../theme/Theme.js";

type DrawerProps = VaulRootProps & {
  withOverlay?: boolean;
  withCloseButton?: boolean;
  withDragHandle?: boolean;
};

const drawerContext = React.createContext<DrawerProps>({
  withOverlay: true,
  withCloseButton: true,
  withDragHandle: false,
});

export const DrawerProvider = drawerContext.Provider;

export const useDrawerContext = () => {
  if (drawerContext === undefined) {
    throw new Error("useDrawerContext must be used within a DrawerProvider");
  }
  return React.useContext(drawerContext);
};

const Drawer = ({
  shouldScaleBackground = true,
  withOverlay = true,
  withCloseButton = true,
  withDragHandle = false,
  direction = "bottom",
  ...props
}: DrawerProps) => (
  <DrawerProvider
    value={{ withOverlay, withCloseButton, withDragHandle, direction }}
  >
    <Vaul.Root
      shouldScaleBackground={shouldScaleBackground}
      direction={direction}
      {...props}
    />
  </DrawerProvider>
);

const DrawerTrigger: React.FC<
  React.ComponentPropsWithoutRef<typeof Vaul.Trigger>
> = Vaul.Trigger;

const DrawerPortal = Vaul.Portal;

const DrawerClose: React.FC<React.ComponentPropsWithoutRef<typeof Vaul.Close>> =
  Vaul.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof Vaul.Overlay>,
  React.ComponentPropsWithoutRef<typeof Vaul.Overlay>
>(({ className, ...props }, ref) => {
  const { primaryColor, secondaryColor } = useThemeContext();

  return (
    <Vaul.Overlay
      ref={ref}
      data-primary-color={primaryColor}
      data-secondary-color={secondaryColor}
      className={clsx("ox-reset ox-VaulOverlay", className)}
      {...props}
    />
  );
});

const DrawerContent: React.FC<
  React.ComponentPropsWithoutRef<typeof Vaul.Content>
> = React.forwardRef<
  React.ElementRef<typeof Vaul.Content>,
  React.ComponentPropsWithoutRef<typeof Vaul.Content>
>(({ className, children, ...props }, ref) => {
  const { withOverlay, withCloseButton, withDragHandle, direction } =
    useDrawerContext();
  const { primaryColor, secondaryColor } = useThemeContext();
  return (
    <DrawerPortal>
      {withOverlay ? <DrawerOverlay /> : null}
      <Vaul.Content
        data-primary-color={primaryColor}
        data-secondary-color={secondaryColor}
        ref={ref}
        className={clsx("ox-reset ox-VaulContent", className)}
        {...props}
      >
        {withDragHandle && direction === "bottom" ? (
          <div data-ox-drag-handle data-dir={direction} aria-hidden />
        ) : null}
        {withCloseButton ? (
          <Flex justify="end">
            <Vaul.Close asChild className="ox-DrawerClose">
              <IconButton size="xs" variant="tertiary" color="gray">
                <X />
              </IconButton>
            </Vaul.Close>
          </Flex>
        ) : null}
        {children}
        {withDragHandle && direction === "top" ? (
          <div data-ox-drag-handle data-dir={direction} aria-hidden />
        ) : null}
      </Vaul.Content>
    </DrawerPortal>
  );
});

const DrawerTitle: React.FC<React.ComponentPropsWithoutRef<typeof Vaul.Title>> =
  Vaul.Title;

const DrawerDescription: React.FC<
  React.ComponentPropsWithoutRef<typeof Vaul.Description>
> = Vaul.Description;

Drawer.displayName = "Drawer";
DrawerContent.displayName = "DrawerContent";
DrawerTitle.displayName = "DrawerTitle";
DrawerDescription.displayName = "DrawerDescription";
DrawerTrigger.displayName = Vaul.Trigger.displayName;
DrawerOverlay.displayName = Vaul.Overlay.displayName;
DrawerClose.displayName = Vaul.Close.displayName;

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
};
